import type { SelectOption } from '@/components/ui/filters/FilterDialog/inputs/types'
import type { FieldSchema } from '@/stores'
import { humanizeText, numberToCurrency } from '@/utils'
import { formatActivityType, formatAssetClass } from '../../labels'

// How each select facet's raw value reads in the dropdown and the active-filter chip. Keeps the
// stored filter value raw (so it still matches the rows) while showing humanized text.
const FACET_LABELERS: Record<string, (value: string) => string> = {
  assetClass: formatAssetClass,
  type: formatActivityType,
  term: humanizeText,
}

const labelFacetValue = (field: string, value: string): string =>
  FACET_LABELERS[field]?.(value) ?? value

// Filter facets for each tab, like the Transactions page's schema. Account leads each schema (keyed
// on the same accountNumber the column shows) so you can scope to one account from inside the table;
// the rest are the tab's own columns.

const currency = (value: number) => numberToCurrency(value)

const accountFacet: FieldSchema = {
  field: 'accountNumber',
  label: 'Account',
  type: 'select',
  multiple: true,
}

export const holdingsFilterSchema: FieldSchema[] = [
  accountFacet,
  {
    field: 'assetClass',
    label: 'Asset class',
    type: 'select',
    multiple: true,
    formatter: formatAssetClass,
  },
  { field: 'symbol', label: 'Symbol', type: 'text' },
  { field: 'description', label: 'Description', type: 'text' },
  {
    field: 'marketValue',
    label: 'Market value',
    type: 'number',
    formatter: currency,
  },
  {
    field: 'gainLoss',
    label: 'Gain / loss',
    type: 'number',
    formatter: currency,
  },
]

export const activityFilterSchema: FieldSchema[] = [
  accountFacet,
  {
    field: 'type',
    label: 'Type',
    type: 'select',
    multiple: true,
    formatter: formatActivityType,
  },
  { field: 'description', label: 'Description', type: 'text' },
  { field: 'date', label: 'Date', type: 'date' },
  { field: 'amount', label: 'Amount', type: 'number', formatter: currency },
]

export const realizedFilterSchema: FieldSchema[] = [
  accountFacet,
  {
    field: 'term',
    label: 'Term',
    type: 'select',
    multiple: true,
    formatter: humanizeText,
  },
  { field: 'description', label: 'Description', type: 'text' },
  { field: 'liquidationDate', label: 'Sale date', type: 'date' },
  {
    field: 'gainLoss',
    label: 'Gain / loss',
    type: 'number',
    formatter: currency,
  },
]

// The distinct values a select facet offers, read off the tab's own rows so the dropdown only lists
// what is present.
export const buildGetUniqueValues =
  <Row>(rows: Row[], selectFields: string[]) =>
  (field: string): SelectOption[] => {
    if (!selectFields.includes(field)) return []

    const values = new Set<string>()
    for (const row of rows) {
      const value = (row as Record<string, unknown>)[field]
      if (value != null && value !== '') values.add(String(value))
    }
    return Array.from(values)
      .sort()
      .map((value) => ({ value, label: labelFacetValue(field, value) }))
  }
